import React from "react";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import { usePropertyContext } from "../../context";
import toTitleCase from "utils/toTitleCase";
import cleanAddress from "utils/cleanAddress";
import Search from "elements/Input/Search";
import { useAutocomplete } from "utils/useAutocomplete";

const PreviousAddressSearch = () => {
	const {
		flowId,
		next,
		setPreviousAddressValue,
		setPreviousAddress2Value,
		setPreviousCityValue,
		setPreviousStateValue,
		setPreviousZipcodeValue,
		setPreviousCountyValue,
		setSpecialFieldsValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();

	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		next();
	};

	const [query, setQuery] = React.useState("");
	const [showResults, setShowResults] = React.useState(false);
	const [activeIndex, setActiveIndex] = React.useState<number>();

	const { autoCompleteLoading, autoCompleteResults } = useAutocomplete(query);

	const handleItemSelect = React.useCallback(
		(index: number) => {
			if (!autoCompleteResults) return;

			const {
				situsfullstreetaddress,
				situscity,
				situsstate,
				situszip5,
				fips,
				apn,
				county,
			} = autoCompleteResults[index].params;

			const addressData = {
				address: cleanAddress(situsfullstreetaddress),
				line2: "",
				city: toTitleCase(situscity),
				state: situsstate,
				zipcode: situszip5,
				fips,
				apn,
				county,
			};

			setPreviousAddressValue(addressData.address);
			setPreviousAddress2Value(addressData.line2);
			setPreviousCityValue(addressData.city);
			setPreviousStateValue(addressData.state);
			setPreviousZipcodeValue(addressData.zipcode);
			setPreviousCountyValue(addressData.county);
			setSpecialFieldsValue({ previous_address_apn: addressData.apn });
			next();
		},
		[autoCompleteResults]
	);

	const handleKeyPress = React.useCallback(
		e => {
			if (!autoCompleteResults.length || !query) return;

			if (e.key === "ArrowUp") {
				e.preventDefault();
				let newIndex;
				if (typeof activeIndex !== "undefined") {
					newIndex =
						activeIndex === 0
							? autoCompleteResults.length - 1
							: activeIndex - 1;
				} else {
					newIndex = autoCompleteResults.length - 1;
				}
				setActiveIndex(newIndex);
			}

			if (e.key === "ArrowDown") {
				e.preventDefault();
				let newIndex;
				if (typeof activeIndex !== "undefined") {
					newIndex =
						activeIndex === autoCompleteResults.length - 1
							? 0
							: activeIndex + 1;
				} else {
					newIndex = 0;
				}
				setActiveIndex(newIndex);
			}
		},
		[activeIndex, autoCompleteResults, query]
	);

	const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = e => {
		setQuery(e.target.value);
	};

	React.useEffect(() => {
		if (!showResults) setActiveIndex(undefined);
	}, [showResults]);

	React.useEffect(() => {
		if (showResults && query) {
			setActiveIndex(undefined);
		}
	}, [showResults, query]);

	React.useEffect(() => {
		const handler = () => {
			setShowResults(false);
		};

		window.addEventListener("click", handler);

		return () => {
			window.removeEventListener("click", handler);
		};
	}, []);

	React.useEffect(() => {
		if (query.length > 1 && autoCompleteResults?.length) {
			setShowResults(true);
		} else if (!query && !autoCompleteResults?.length) {
			setShowResults(false);
		}
	}, [autoCompleteResults, query]);

	return (
		<form
			className="pb-6"
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Previous Address</p>
				</div>

				<div
					id="exemption-address-autocomplete-container"
					className={[showResults ? "show-results" : ""].join(" ")}>
					<Search
						autoComplete="off"
						required={false}
						showRightIcon={false}
						onChange={handleInputChange}
						onKeyDown={handleKeyPress}
						placeholder="Enter your property address"
					/>
					{showResults && (
						<div id="exemption-address-autocomplete-results-wrapper">
							<div
								id="exemption-address-autocomplete-results-container"
								onScrollCapture={e => {
									e.preventDefault();
									e.stopPropagation();
								}}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
								}}>
								{autoCompleteResults
									?.slice(0, 2)
									.map(({ label, params }, i) => {
										const showParcel =
											autoCompleteResults[i - 1]?.label === label ||
											autoCompleteResults[i + 1]?.label === label;

										return (
											<div
												key={label + i}
												className={
													"address-autocomplete-item" +
													(i === activeIndex ? "-active" : "")
												}
												onMouseOver={() => setActiveIndex(i)}
												onClick={() => handleItemSelect(i)}>
												<p className="no-translate">{label}</p>
												{showParcel && (
													<label>
														Parcel:{" "}
														<span className="no-translate">{params.apn}</span>
													</label>
												)}
											</div>
										);
									})}
							</div>
						</div>
					)}
				</div>
			</div>
		</form>
	);
};

export default PreviousAddressSearch;
